/** @format */

import dayjs from 'dayjs';
import { FRAMES } from '../Constants/commons';

export const isEmpty = (value) => {
  if (value === undefined || value === null) {
    return true;
  }
  if (value && value.trim().length > 0) {
    return false;
  } else {
    return true;
  }
};

export const isNumber = (value) => {
  return isNaN(value.trim()) ? false : true;
};

// fromDate: instead of going from now (system) in past, we can choose the from custom date
export const getRollingTimeFrames = function (fromDate) {
  if (!fromDate) return FRAMES;

  const date = dayjs(fromDate);

  return FRAMES.map((frame) => ({
    ...frame,
    to: dayjs(fromDate),
    from: date
      .subtract(
        parseInt(frame.title) || 1,
        frame.title.endsWith('D')
          ? 'day'
          : frame.title.endsWith('W')
            ? 'week'
            : frame.title.endsWith('M')
              ? 'month'
              : frame.title.endsWith('Y')
                ? 'year'
                : 'day'
      )
      .startOf('day'),
  }));
};

/* construct timeframes based on fromDate to decide in which timeframe the selectedDate falls
   in which quarter
   in which year
   in which month
   in which week
   in which day
*/
export const getCalendarTimeFrames = function (fromDate) {
  if (!fromDate) return FRAMES;

  // Clone the date to avoid modifying the original
  const date = dayjs(fromDate);

  const getQuarterRange = (d) => {
    const quarter = Math.floor((d.month() + 3) / 3);
    const firstMonth = (quarter - 1) * 3;
    const lastMonth = firstMonth + 2;

    return {
      start: d.month(firstMonth).date(1).startOf('day'),
      end: d.month(lastMonth).endOf('month').endOf('day'),
    };
  };

  return FRAMES.map((frame) => {
    switch (frame.title) {
      case '1Y': {
        const start = date.month(0).date(1).startOf('day');
        const end = date.month(11).date(31).endOf('day');
        return {
          ...frame,
          from: start,
          to: end,
        };
      }

      case '3M': {
        const { start, end } = getQuarterRange(date);
        return {
          ...frame,
          from: start,
          to: end,
        };
      }

      case '1M': {
        const start = date.date(1).startOf('day');
        const end = date.endOf('month').endOf('day');
        return {
          ...frame,
          from: start,
          to: end,
        };
      }

      case '1W': {
        const start = date.startOf('week').startOf('day');
        const end = date.endOf('week').endOf('day');
        return {
          ...frame,
          from: start,
          to: end,
        };
      }

      case '1D':
      default: {
        return {
          ...frame,
          from: date.startOf('day'),
          to: date.startOf('day').endOf('day'),
        };
      }
    }
  });
};

export const getFinancialTimeFrames = function (fromDate) {
  if (!fromDate) return FRAMES;

  // Clone the date to avoid modifying the original
  const date = dayjs(fromDate);

  // Get financial year (April to March)
  const getFinancialYearRange = (d) => {
    const year = d.month() >= 3 ? d.year() : d.year() - 1; // Financial year starts in April
    return {
      start: dayjs().year(year).month(3).date(1).startOf('day'), // April 1st of financial year
      end: dayjs()
        .year(year + 1)
        .month(2)
        .date(31)
        .endOf('day'), // March 31st of next calendar year
    };
  };

  // Get financial quarter (3-month periods starting April)
  const getFinancialQuarterRange = (d) => {
    const month = d.month();
    const year = d.year();

    // Calculate financial quarter (0-3, where 0 = Q1, 1 = Q2, etc.)
    const financialQuarter = Math.floor(((month - 3 + 12) % 12) / 3);
    const startMonth = (financialQuarter * 3 + 3) % 12; // 3, 6, 9, 0 (Apr, Jul, Oct, Jan)
    const endMonth = (startMonth + 2) % 12; // Last month of the quarter

    // Adjust year for Q4 (Jan-Mar)
    const startYear = startMonth === 0 ? year - 1 : year;
    const endYear = startMonth === 9 ? year + 1 : year; // For Q4 (Jan-Mar), end year is same as calendar year

    return {
      start: dayjs().year(startYear).month(startMonth).date(1).startOf('day'),
      end: dayjs().year(endYear).month(endMonth).endOf('month').endOf('day'),
    };
  };

  return FRAMES.map((frame) => {
    switch (frame.title) {
      case '1Y': {
        const { start, end } = getFinancialYearRange(date);
        return {
          ...frame,
          from: start,
          to: end,
        };
      }

      case '3M': {
        const { start, end } = getFinancialQuarterRange(date);
        return {
          ...frame,
          from: start,
          to: end,
        };
      }

      // For 1M, 1W, and 1D, we can use the same logic as calendar
      case '1M': {
        const start = date.date(1).startOf('day');
        const end = date.endOf('month').endOf('day');
        return {
          ...frame,
          from: start,
          to: end,
        };
      }

      case '1W': {
        const start = date.startOf('week').startOf('day');
        const end = date.endOf('week').endOf('day');
        return {
          ...frame,
          from: start,
          to: end,
        };
      }

      case '1D':
      default: {
        return {
          ...frame,
          from: date.startOf('day'),
          to: date.startOf('day').endOf('day'),
        };
      }
    }
  });
};

export const formatRevenue = (value) => {
  return (value / 1000).toLocaleString('en-IN', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  });
};

export const currentYearShort = String(new Date().getFullYear()).slice(2);

export const getCellColor = (perfObjOrVal) => {
  if (perfObjOrVal == null) return '#64748b';

  if (typeof perfObjOrVal === 'object') {
    if (perfObjOrVal?.perf?.color) {
      return perfObjOrVal.perf.color;
    }

    if (perfObjOrVal?.trend === 'up') return '#16a34a';
    if (perfObjOrVal?.trend === 'down') return '#dc2626';

    const val = perfObjOrVal.value ?? perfObjOrVal;
    if (Number(val) < 0) return '#dc2626';
    return '#64748b';
  }

  if (typeof perfObjOrVal === 'string' && perfObjOrVal === 'up') {
    return '#16a34a';
  }

  if (typeof perfObjOrVal === 'string' && perfObjOrVal === 'down') {
    return '#dc2626';
  }

  if (Number(perfObjOrVal) < 0) return '#dc2626';
  return '#64748b';
};

export const getYTDMonths = () => {
  const monthNames = [
    'Jan',
    'Feb',
    'Mar',
    'Apr',
    'May',
    'Jun',
    'Jul',
    'Aug',
    'Sep',
    'Oct',
    'Nov',
    'Dec',
  ];
  const currentMonthIndex = dayjs().month();
  return monthNames.slice(0, currentMonthIndex + 1);
};

export const format = (val) =>
  val?.toLocaleString(undefined, { minimumFractionDigits: 2 }) ?? '-';

export function toThousands(val) {
  return Math.round((Number(val) || 0) / 1000);
}

export function toThousandsDecimal(val) {
  return (Number(val) || 0) / 1000;
}

export function toInt(val) {
  return Math.round(val || 0);
}

export function sum(data, key) {
  return data.reduce((acc, item) => acc + (item[key] || 0), 0);
}

export function getPercentage(current, base) {
  if (!base) return '';
  return ((current / base - 1) * 100).toFixed(1) + '%';
}

export function getPercentageNumber(current, base) {
  if (!base) return 0;
  return (current / base) * 100;
}

export function getExpensePerformance(current, budget) {
  if (budget === undefined || budget === null)
    return { color: '#dc2626', trend: '' };
  if (current > budget) return { color: '#dc2626', trend: 'down' };
  if (current <= budget) return { color: '#16a34a', trend: 'up' };
  return { color: '#dc2626', trend: '' };
}

export function getRevenuePerformance(current, budget) {
  if (
    current === undefined ||
    current === null ||
    budget === undefined ||
    budget === null
  ) {
    return { color: '#dc2626', trend: '' };
  }

  if (current > budget) return { color: '#16a34a', trend: 'up' };
  if (current < budget) return { color: '#dc2626', trend: 'down' };
  return { color: '#dc2626', trend: '' };
}

export function getPerformance(pct) {
  if (pct > 100) return { color: '#16a34a', trend: 'up' };
  return { color: '#dc2626', trend: 'down' };
}

export function negatePercentage(pct) {
  if (!pct || typeof pct !== 'string') return pct;
  const match = pct.match(/^(-?)(\d+(?:\.\d+)?)%$/);
  if (!match) return pct;
  const sign = match[1] === '-' ? '' : '-';
  const num = match[2];
  return `${sign}${num}%`;
}

export const getDepartmentNames = (activeTab) => {
  const deptNames = {
    hotel: 'Hotel',
    rooms: 'Rooms',
    fnb: 'F&B',
    spa: 'Spa',
    mod: 'MOD',
    others: 'MOD',
    admin: 'Admin',
    it: 'IT',
    rm: 'R&M',
    sm: 'S&M',
    utilities: 'Utilities',
    payroll: 'Payroll',
    headcount: 'Headcount',
  };
  return deptNames[activeTab] || '';
};

export const isAllZeroCardData = (card) => {
  if (!card) return true;

  if (card && Array.isArray(card.rows) && typeof card.value === 'number') {
    return false;
  }

  const toNum = (v) => {
    const n = typeof v === 'string' ? Number(v.replace(/,/g, '')) : Number(v);

    return Number.isFinite(n) ? n : 0;
  };

  const nums = [];

  if (Array.isArray(card.rows)) {
    card.rows.forEach((row) => {
      if (!row) return;

      if ('value' in row) {
        nums.push(toNum(row.value));
      }
    });
  }

  if (Array.isArray(card.secondaryRows)) {
    card.secondaryRows.forEach((row) => {
      if (!row) return;

      if ('value' in row) {
        nums.push(toNum(row.value));
      }

      if (Array.isArray(row.values)) {
        row.values.forEach((value) => {
          if (value && typeof value === 'object' && 'value' in value) {
            nums.push(toNum(value.value));
          } else {
            nums.push(toNum(value));
          }
        });
      }
    });
  }

  if (Array.isArray(card.roomRows)) {
    card.roomRows.forEach((row) => {
      if (!row || !Array.isArray(row.values)) return;

      row.values.forEach((cell) => {
        if (cell && typeof cell === 'object' && 'value' in cell) {
          nums.push(toNum(cell.value));
        } else {
          nums.push(toNum(cell));
        }
      });
    });
  }

  if (nums.length === 0) {
    return true;
  }

  return nums.every((n) => n === 0);
};
